import { get, uniq, compact } from 'lodash';
import moment from 'moment';
import { getCentreByIds } from './CentreService';
import {
  childService,
  guardianService,
  userService,
  centreSerivce,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
} from '../utils/msEndpointConstants';
import DatadogHandler from '../utils/datadog';
import { DEFAULT_ERROR, SAVE_DATE_FORMAT } from '../utils/constants';
import {
  retrieveListOrObject,
  retrieveObjectMapper,
  retrieveSortForMS,
} from '../utils';
import { transformGetAllGuardianV2 } from '../utils/ms-utils/ms-phase-3/GuardianUtils';
import {
  getChildByIds,
  getChildCurrentLevelsByChildIds,
  getChildEnrolmentStatusByChildIds,
} from './ChildService';
import { getCodeByCodeIds, getLevelByIds } from './SchoolConfigService';

const fileName = 'src/services/GuardianService.js';
const { GUARDIAN, USER, CHILD: CHILD_P3 } = MS_PHASE_3_ENDPOINTS;
const { CHILD, CENTRE } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;
const guardianServiceReq = guardianService();
const userServiceReq = userService();

export const msUpdateChildGuardian = async (_, reqData) => {
  try {
    const dto = reqData?.variables?.dto || {};
    if (!dto?.ID) {
      return {
        success: true,
        data: {
          updateChildGuardianV2: {},
        },
      };
    }

    const reqBody = {
      child_relations:
        dto?.childRelations?.map(cR => ({
          child_id: cR.childID,
          other_relation_text: cR?.otherRelationText,
          relation: cR?.relation,
          ...(cR?.from && {
            from: moment(cR?.from).format(SAVE_DATE_FORMAT),
          }),
          ...(cR?.to && {
            to: moment(cR?.to).format(SAVE_DATE_FORMAT),
          }),
        })) || [],
      guardian_info: {
        email: dto?.guardianInfo?.email,
        firstname: dto?.guardianInfo?.firstname,
        lastname: dto?.guardianInfo?.lastname,
        identification_no: dto?.guardianInfo?.identificationNo,
        mobile_phone: dto?.guardianInfo?.mobilePhone,
        mobile_phone_country_code: dto?.guardianInfo?.mobilePhoneCountryCode,
        ...(dto?.guardianInfo?.icBackImageKeyPreSignedUrl && {
          ic_back_image_key: dto?.guardianInfo?.icBackImageKeyPreSignedUrl,
        }),
        ...(dto?.guardianInfo?.icFrontImageKeyPreSignedUrl && {
          ic_front_image_key: dto?.guardianInfo?.icFrontImageKeyPreSignedUrl,
        }),
        ...(dto?.guardianInfo?.profilePhotoKey && {
          profile_photo_key: dto?.guardianInfo?.profilePhotoKey,
        }),
      },
      id: dto?.ID,
    };
    const result = await guardianServiceReq.post(
      ...GUARDIAN.updateChildGuardian(reqBody)
    );
    return {
      success: true,
      data: {
        updateChildGuardianV2: result?.data && {
          ...retrieveObjectMapper(result?.data, [
            'ID:id',
            'email',
            'mobile_phone',
            'mobile_phone_country_code',
          ]),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildGuardian',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            error?.response?.data?.error || error?.message || DEFAULT_ERROR,
          errCode: error?.response?.data?.errCode || error?.code,
        },
      ],
    };
  }
};

export const msUpdateParentEmailOrMobile = async (_, reqData) => {
  try {
    const { IDParent, parentUpdate } = reqData?.variables || {};
    if (!IDParent) {
      return {
        success: false,
        data: {
          updateParentEmailOrMobile: {},
        },
      };
    }

    const reqBody = {
      parent_id: IDParent,
      ...(parentUpdate?.email !== undefined && {
        email: parentUpdate.email,
      }),
      ...(parentUpdate?.mobilePhone !== undefined && {
        mobile_phone: parentUpdate.mobilePhone,
      }),
      ...(parentUpdate?.mobilePhoneCountryCode !== undefined && {
        mobile_phone_country_code: parentUpdate.mobilePhoneCountryCode,
      }),
    };

    const result = await userServiceReq.post(
      ...USER.updateParentEmailOrMobile(reqBody)
    );

    return {
      success: true,
      data: {
        updateParentEmailOrMobile: result?.data && {
          ...retrieveObjectMapper(result?.data, [
            'ID:id',
            'email',
            'mobilePhone:mobile_phone',
            'mobilePhoneCountryCode:mobile_phone_country_code',
          ]),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateParentEmailOrMobile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            error?.response?.data?.error || error?.message || DEFAULT_ERROR,
          errCode: error?.response?.data?.errCode || error?.code,
        },
      ],
    };
  }
};

export const getChildDetailsForGuardian = async (fkChilds = []) => {
  try {
    const childSvc = childService();
    const childResp = await childSvc
      .post(...CHILD.GET_CHILD_BY_IDS(fkChilds))
      .catch(() => ({}));
    let children = childResp?.data?.data || [];
    if (children?.length > 0) {
      const [currentLevelResp, childLevelResp, enrolmentStatusResp] =
        await Promise.all([
          childSvc
            .post(...CHILD_P3.getCurrentChildLevel(fkChilds))
            .catch(() => ({})),
          childSvc
            .post(...CHILD_P3.getChildLevelsByChildIds(fkChilds))
            .catch(() => ({})),
          childSvc
            .post(...CHILD_P3.getEnrolmentStatusByChildIds(fkChilds))
            .catch(() => ({})),
        ]);
      const currentLevel = currentLevelResp?.data?.data || {};
      let childLevels = childLevelResp?.data?.data || [];
      const enrolmentStatus = enrolmentStatusResp?.data?.data || {};
      const fkCentres = uniq(
        compact(childLevels?.map(dt => dt?.fk_centre) || [])
      );

      if (fkCentres?.length > 0) {
        const centreSvc = centreSerivce();
        const centreResp = await centreSvc
          .post(...CENTRE.GET_CENTRE_BY_IDS(fkCentres))
          .catch(() => ({}));
        const centreData = centreResp?.data?.data || [];
        childLevels = childLevels?.map(cL => ({
          ...cL,
          centre: centreData?.find(({ id }) => id === cL?.fk_centre),
        }));
      }

      children = children?.map(ch => ({
        ...ch,
        currentLevel: currentLevel?.[ch?.id] || null,
        enrolmentStatus: enrolmentStatus?.[ch?.id] || null,
        childLevels:
          childLevels?.filter(({ fk_child: fkChild }) => fkChild === ch?.id) ||
          [],
      }));
    }
    return children;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getChildDetailsForGuardian',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getGuardianChildRelationByGuardianIds = (
  guardianIds = [],
  params = {}
) => {
  if (guardianIds.length === 0) {
    return {};
  }
  return guardianService()
    .post(...GUARDIAN.getGuardianChildRelations(guardianIds, params))
    .then(res => retrieveListOrObject(res, false))
    .catch(() => ({}));
};

export const getGuardianCentreVerificationByGuardianIds = (
  guardianIds = []
) => {
  if (guardianIds.length === 0) {
    return {};
  }
  return guardianService()
    .post(...GUARDIAN.getCentreVerifications(guardianIds))
    .then(res => retrieveListOrObject(res, false))
    .catch(() => ({}));
};

export const msFetchAllGuardians = async (_, reqData) => {
  try {
    const { filter, pagination, centreID } = reqData;
    const reqBody = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.ftsInput && {
        fts_input: reqData?.ftsInput,
      }),
      ...(filter?.ID && {
        filter: {
          id: [{ eq: +filter?.ID }],
        },
      }),
    };
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || 10,
      ...(pagination?.sort && {
        ...retrieveSortForMS(pagination?.sort || []),
      }),
    };
    const guardianSvc = guardianService();
    const resp = await guardianSvc
      .post(...GUARDIAN.getAllGuardianV2(reqBody, params))
      .catch(() => ({}));

    const data = get(resp, 'data.data') || [];
    const guardianIds = uniq(compact(data?.map(dt => dt?.id) || []));
    const [childRelations, centreVerifications] = await Promise.all([
      getGuardianChildRelationByGuardianIds(guardianIds, { active: 'both' }),
      getGuardianCentreVerificationByGuardianIds(guardianIds),
    ]);

    const centreIds = uniq([
      ...compact(
        Object.values(centreVerifications).flatMap(item =>
          item?.guardian_centre_verification?.data?.map(dt => dt?.fk_centre)
        )
      ),
    ]);

    const centres = await getCentreByIds(centreIds);

    return transformGetAllGuardianV2({
      resp,
      childRelations,
      centreVerifications,
      centres,
      centreID,
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllGuardians',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllSearchGuardians = async (_, reqData) => {
  try {
    const { filter, pagination } = reqData;
    const reqBody = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.ftsInput && {
        fts_input: reqData?.ftsInput,
      }),
      ...(filter?.ID && {
        filter: {
          id: [{ eq: +filter?.ID }],
        },
      }),
    };
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || 10,
      ...(pagination?.sort && {
        ...retrieveSortForMS(pagination?.sort || []),
      }),
    };
    const guardianSvc = guardianService();
    const resp = await guardianSvc
      .post(...GUARDIAN.getAllGuardianV2(reqBody, params))
      .catch(() => ({}));

    const data = get(resp, 'data.data') || [];
    const guardianIds = uniq(compact(data?.map(dt => dt?.id) || []));
    const [childRelations, centreVerifications] = await Promise.all([
      getGuardianChildRelationByGuardianIds(guardianIds, { active: 'both' }),
      getGuardianCentreVerificationByGuardianIds(guardianIds),
    ]);

    const childIds = uniq(
      compact(
        Object.values(childRelations).flatMap(item =>
          item?.data?.map(dt => dt?.fk_child)
        )
      )
    );

    const [currentLevels, childLevels] = await Promise.all([
      getChildCurrentLevelsByChildIds(childIds),
      childService()
        .post(CHILD_P3.getChildLevelsByFilter, {
          child_id: [{ eq: childIds }],
        })
        .then(retrieveListOrObject)
        .catch(() => []),
    ]);

    const centreIds = uniq([
      ...compact(
        Object.values(centreVerifications).flatMap(item =>
          item?.guardian_centre_verification?.data?.map(dt => dt?.fk_centre)
        )
      ),
      ...compact(Object.values(currentLevels).map(cl => cl?.fk_centre)),
      ...compact(childLevels?.map(cl => cl?.fk_centre)),
    ]);

    const levelIds = uniq(
      compact(Object.values(currentLevels).map(cl => cl?.id))
    );

    const relationCodeIds = uniq(
      compact(
        Object.values(childRelations).flatMap(item =>
          item?.data?.map(dt => dt?.fk_relation)
        )
      )
    );

    const [centres, children, codes, levels, enrolmentStatus] =
      await Promise.all([
        getCentreByIds(centreIds),
        getChildByIds(childIds),
        getCodeByCodeIds(relationCodeIds),
        getLevelByIds(levelIds),
        getChildEnrolmentStatusByChildIds(childIds),
      ]);

    return transformGetAllGuardianV2({
      resp,
      childRelations,
      centreVerifications,
      children,
      currentLevels,
      centres,
      levels,
      childLevels,
      enrolmentStatus,
      codes,
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllSearchGuardians',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetGuardianActivityLog = async (_, reqData = {}) => {
  try {
    if (!reqData?.guardianID) return { data: {}, success: true };

    const { pagination } = reqData;
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || 10,
      ...(pagination?.sort && {
        ...retrieveSortForMS(pagination?.sort || [], 'sort'),
      }),
    };

    const guardianSvc = guardianService();
    const resp = await guardianSvc.get(
      GUARDIAN.getGuardianActivityLog(reqData?.guardianID, params)
    );

    const data =
      resp?.data?.data?.map(log => ({
        ...(log?.creator && {
          Creator: retrieveObjectMapper(log?.creator, [
            'firstname',
            'lastname',
          ]),
        }),
        ...(log?.activity_log && {
          ActivityLog: retrieveObjectMapper(log?.activity_log, [
            'data',
            'audit_action',
            'created_at',
          ]),
        }),
      })) || [];

    return {
      success: true,
      data: {
        GetGuardianActivityLog: {
          totalCount: resp?.data?.total_count || 0,
          data,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllSearchGuardians',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: {
        message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
        errCode: ex?.response?.data?.errCode || ex?.code,
      },
    };
  }
};
